import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InvoiceCategoryTabs from '../../../components/Billing/Invoice/InvoiceCategoryTabs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ReactComponent as FunnelIcon } from '../../../assets/SVG/Filter Icon Box.svg';
import NewInvoiceButton from '../../../components/Billing/Invoice/NewInvoiceButton';
import ResetButton from '../../../components/common/ResetButton';
import InvoiceFilter from '../../../components/Billing/Invoice/InvoiceFilter';
import InvoiceTable from '../../../components/Billing/Invoice/InvoiceTable';
import InvoicePagination, { InvoiceStatusChipGroup } from '../../../components/Billing/Invoice/InvoicePagination';
import { useInvoicesQuery, usePatientDrugs } from './invoiceHelper';
import { useClinic } from '../../../components/ClinicProvider';
import { Chip } from '@mui/material';
import useCurrency from '../../../components/CurrencyProvider';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';
import { usePatientContext } from '../../../components/SubPatinetProvider';
import { formatDateDMY } from '../../../utils/dateUtils';
import { useUnbilledServicesByPatient } from './newInvoice/newInvoiceForm.helper';
import { UnbilledInvoiceService } from './newInvoice/newInvoice.types';
import UnbilledServicesTable from './components/UnbilledServicesTable';
import PatientDrugsTable from './components/PatientDrugsTable';
import UnsettledDonorBillDetector from './newInvoice/UnsettledDonorBillPopup';

// --- Types ---
interface InvoiceFilterValues {
  fromDate: string;
  toDate: string;
  billType: string;
}

// --- Constants ---
const DEFAULT_LIMIT = 10;

// --- Main Invoice Page ---
const PatientInvoice = () => {
  // --- State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_LIMIT);
  const [selectedStatus, setSelectedStatus] = useState<{ value: string; count: number } | null>(null);
  const [filterOpenLegacy, setFilterOpenLegacy] = useState(false); // Modal legacy
  const [filterValues, setFilterValues] = useState<InvoiceFilterValues>({ fromDate: '', toDate: '', billType: '' });
  const { patientId } = usePatientContext();
  
  // By Services tab state
  const [activeTab, setActiveTab] = useState<string>('By Order');
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  // By Pharmacy tab state (India - prescribed items)
  const [selectedDrugIndices, setSelectedDrugIndices] = useState<Set<number>>(new Set());
  
  // Filter handlers
  const handleOpenLegacyFilter = () => setFilterOpenLegacy(true);  // Legacy Modal
  const handleCloseLegacyFilter = () => setFilterOpenLegacy(false);

  const handleApplyFilter = (values: InvoiceFilterValues) => {
    if (
      values.fromDate !== filterValues.fromDate ||
      values.toDate !== filterValues.toDate ||
      values.billType !== filterValues.billType
    ) {
      setFilterValues(values);
      setCurrentPage(1); // Reset to first page on filter
    }
  };

  const navigate = useNavigate();
  const { ClinicId } = useClinic();
  const { currencySymbol } = useCurrency();
  const location = useLocation();
  // const { showLoader, hideLoader } = useLoader();

  // Restore active tab from navigation state (when coming back from create invoice)
  useEffect(() => {
    const state = location.state as { activeTab?: string } | null;
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
    }
  }, [location.state]);
  
  // --- Handlers ---
  const handleStatusSelect = useCallback((status: { value: string; count: number }) => {
    if (status.value !== selectedStatus?.value) {
      setSelectedStatus(status);
      setCurrentPage(1); // Reset to first page on status change
    }
  }, [selectedStatus]);
  const handleNewInvoice = useCallback(() => { navigate(`/admin/billing/invoice/new/${patientId}`, { state: { showTabs: true } }); }, [navigate, patientId]);
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // Reset handler
  const handleReset = useCallback(() => {
    setSelectedStatus(null);
    setFilterValues({ fromDate: '', toDate: '', billType: '' });
    setPageSize(DEFAULT_LIMIT);
    setCurrentPage(1);
    setActiveTab('By Order');
    setSelectedIndices(new Set());
    setSelectedDrugIndices(new Set());
  }, []);

  // Query - main invoices
  const { data, isLoading, error } = useInvoicesQuery({
    clinic: Number(ClinicId),
    search_query: undefined,
    patient_id: patientId,
    from_date: filterValues.fromDate || undefined,
    to_date: filterValues.toDate || undefined,
    bill_type: (['Clinical', 'PH', 'Pharmacy', 'Package', 'Order'].includes(filterValues.billType) ? filterValues.billType : undefined) as 'Clinical' | 'Pharmacy' | 'PH' | 'Package' | 'Order' | undefined,
    status: selectedStatus?.value?.toLowerCase() || undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    enabled: !!ClinicId && activeTab !== 'By Services' && activeTab !== 'By Pharmacy'
  });

  // Query - unbilled services for "By Services" tab
  const { data: unbilledServicesData, isLoading: unbilledServicesLoading } = useUnbilledServicesByPatient(
    activeTab === 'By Services' ? patientId : undefined
  );

  // TODO: TEMPORARY - Remove after testing. 
  // Set in browser console: localStorage.setItem('TEMP_TEST_UAE_DISPENSE_FLOW', 'true') for UAE dispense flow
  // Set in browser console: localStorage.setItem('TEMP_TEST_UAE_DISPENSE_FLOW', 'false') for India prescribed flow
  // Default: false (India prescribed flow) if not set
  const isUAEDispenseFlow = localStorage.getItem('TEMP_TEST_UAE_DISPENSE_FLOW') === 'true' || false;

  // Query - patient drugs for "By Pharmacy" tab (India flow - prescribed items)
  const { data: patientDrugsData, isLoading: patientDrugsLoading } = usePatientDrugs(
    activeTab === 'By Pharmacy' && !isUAEDispenseFlow ? patientId : undefined
  );

  // Query - dispensed invoices for "By Pharmacy" tab (UAE/Dubai flow)
  // Uses existing invoices API with invoice_status_type=dispensed
  const { data: dispensedInvoicesData, isLoading: dispensedInvoicesLoading } = useInvoicesQuery({
    clinic: Number(ClinicId),
    patient_id: patientId,
    status: 'dispensed',
    limit: 100, // Get all dispensed items
    offset: 0,
    enabled: !!ClinicId && activeTab === 'By Pharmacy' && isUAEDispenseFlow
  });

  const patientDrugs = useMemo(() => {
    return patientDrugsData?.objects ?? [];
  }, [patientDrugsData?.objects]);

  const dispensedItems = useMemo(() => {
    return dispensedInvoicesData?.objects ?? [];
  }, [dispensedInvoicesData?.objects]);

  // Combined loading state for By Pharmacy tab
  const pharmacyTabLoading = isUAEDispenseFlow ? dispensedInvoicesLoading : patientDrugsLoading;

  // Transform unbilled services for display
  const unbilledServices = useMemo(() => {
    if (!unbilledServicesData?.services) return [];
    return unbilledServicesData.services;
  }, [unbilledServicesData?.services]);

  // Handle service selection toggle
  const handleServiceToggle = useCallback((index: number) => {
    setSelectedIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Handle select all services
  const handleSelectAllServices = useCallback((services: UnbilledInvoiceService[]) => {
    setSelectedIndices((prev) => {
      // Check if ALL are currently selected
      const allSelected = services.length > 0 && prev.size === services.length;
      if (allSelected) {
        return new Set();
      }
      return new Set(services.map((_, idx) => idx));
    });
  }, []);

  // Navigate to create invoice with selected services
  const handleCreateInvoiceWithServices = useCallback(() => {
    // Get selected services by index and pass them to the new invoice page
    const selectedServices = unbilledServices.filter((_, idx) => selectedIndices.has(idx));
    navigate(`/admin/billing/invoice/new/${patientId}`, { 
      state: { 
        fromByServices: true,
        ...(selectedServices.length > 0 && { selectedServices }),
      } 
    });
  }, [navigate, patientId, selectedIndices, unbilledServices]);

  // Handle drug selection toggle
  const handleDrugToggle = useCallback((index: number) => {
    setSelectedDrugIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Handle select all drugs
  const handleSelectAllDrugs = useCallback(() => {
    setSelectedDrugIndices((prev) => {
      const allSelected = patientDrugs.length > 0 && prev.size === patientDrugs.length;
      if (allSelected) {
        return new Set();
      }
      return new Set(patientDrugs.map((_, idx) => idx));
    });
  }, [patientDrugs]);

  // Navigate to new invoice (Pharmacy) with selected drugs
  const handleNavigateToPharmacyDispense = useCallback(() => {
    if (isUAEDispenseFlow) {
      navigate(`/admin/billing/invoice/new/${patientId}`, {
        state: {
          pharmacyOnly: true,
          fromByPharmacy: true,
        }
      });
    } else {
      // India flow: Navigate with selected prescribed drugs
      const selectedDrugs = patientDrugs.filter((_, idx) => selectedDrugIndices.has(idx));
      navigate(`/admin/billing/invoice/new/${patientId}`, {
        state: {
          pharmacyOnly: true,
          fromByPharmacy: true,
          ...(selectedDrugs.length > 0 && { selectedDrugs }),
        }
      });
    }
  }, [navigate, patientId, isUAEDispenseFlow, patientDrugs, selectedDrugIndices]);

  // Invoices list from API (memoized to keep stable reference)
  const apiInvoices = useMemo(() => data?.objects ?? [], [data?.objects]);

  // DEBUG: Log when data changes to track appending issue
  useEffect(() => {
    
    // eslint-disable-next-line no-console
    console.log('[PatientInvoice] Data changed:', {
      objectsCount: data?.objects?.length,
      totalCount: data?.meta?.total_count,
      billTypeFilter: filterValues.billType,
      statusFilter: selectedStatus?.value,
      firstInvoiceId: data?.objects?.[0]?.id,
      lastInvoiceId: data?.objects?.[data?.objects?.length - 1]?.id,
    });
  }, [data, filterValues.billType, selectedStatus?.value]);

  // Status chips from meta.status_counts
  const statusSummary = useMemo(() => {
    const counts = data?.meta.status_counts;
    if (!counts) return [];
    return [
      { value: 'Billed', count: counts.billed ?? 0 },
      { value: 'Unbilled', count: counts.unbilled ?? 0 },
      { value: 'Draft', count: counts.draft ?? 0 },
      { value: 'Unsettled', count: counts.unsettled ?? 0 },
      { value: 'Dispensed', count: counts.dispensed ?? 0 },
    ];
  }, [data]);

  // Optional client-side fallback filtering
  // Helper to match bill types (normalize PH/Pharmacy)
  const matchesBillType = (invBillType: string | null | undefined, filterBillType: string) => {
    if (!filterBillType) return true;
    const inv = (invBillType || '').toString().toLowerCase();
    const filter = filterBillType.toString().toLowerCase();
    if ((inv === 'ph' || inv === 'pharmacy') && (filter === 'ph' || filter === 'pharmacy')) return true;
    return inv === filter;
  };

  const displayedInvoices = useMemo(() => {
    // Start from server results
    let list = apiInvoices;

    // Ensure returned invoices match the requested bill_type (defensive)
    if (filterValues.billType) {
      list = list.filter(inv => matchesBillType(inv.bill_type, filterValues.billType));
    }

    return list;
  }, [apiInvoices, filterValues.billType]);

  // Pagination from total_count
  const totalPages = useMemo(() => {
    const total = data?.meta.total_count || 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [data, pageSize]);

  // Replace InvoiceTable data prop
  // NOTE: Adapt InvoiceTable to expect fields from InvoiceItem (patient, visit_date, etc.)
  // If it still expects mock shape, map here.
  // Helper: normalize status from API to table expected casing
  const normalizeStatus = (s: string): 'Billed' | 'Unsettle' | 'Unbilled' | 'Draft' | 'Dispensed' => {
    const v = (s || '').toLowerCase();
    switch (v) {
    // Billed/settled states
    case 'billed':
    case 'paid':
      return 'Billed';
    case 'settled':
      return 'Billed';
    // Needs settlement / partially paid
    case 'unsettled':
    case 'unpaid':
    case 'partially_paid':
    case 'partial':
      return 'Unsettle';
    // Not billed yet
    case 'unbilled':
    case 'pending':
      return 'Unbilled';
    case 'draft':
      return 'Draft';
    // Pharmacy dispensed
    case 'dispensed':
      return 'Dispensed';
    default:
      return 'Unbilled';
    }
  };

  const mapBillTypeLabel = (billType: string | null | undefined): string => {
    if (!billType) return '-';
    if (billType === 'PH') return 'Pharmacy';
    return billType;
  };

  const tableData = useMemo(
    () =>
      displayedInvoices.map(inv => ({
        // API-style identifiers (required by InvoiceData)
        patient_id: inv.patient_id,
        appointment_id: inv.appointment_id ?? null,
        id: inv.id ?? null,

        // camelCase aliases for convenience (also in InvoiceData)
        patientId: inv.patient_id,
        mrn: inv.patient_mrn_no ?? undefined,
        appointmentId: inv.appointment_id ?? null,

        date: inv.visit_date || '-',
        patientName: `${inv.patient ?? inv?.patient_name ?? ''}${inv.age ? ` | ${inv.age}` : ''}` || '-',
        status: normalizeStatus(inv.status || inv.type || ''),
        billNo: inv.bill_number || '-',
        clinic: inv?.clinic || '-', 
        billType: mapBillTypeLabel(inv.bill_type),
        provider: inv.Provider || '-',
        invoiceDueStatus: inv.invoice_due_status ?? '',
        totalAmt: inv.total_amount != null ? String(inv.total_amount) : '-',
        concessionAmt: inv.concession_amount != null ? String(inv.concession_amount) : '-',
        netAmt: inv.net_amount != null ? String(inv.net_amount) : '-',
        paidAmt: inv.paid_amount != null ? String(inv.paid_amount) : '-',
        balanceAmt: inv.balance_amount != null ? String(inv.balance_amount) : '-',
        billRemark: inv.remark ,
        actions: '',
        paymentSource: inv.payment_source ?? null,
        label: inv.label ?? null,
        labelMrnNo: inv.label_mrn_no ?? null,
        can_settle_invoice: inv.can_settle_invoice ?? true,
        invoice_patient_type: inv.invoice_patient_type ?? null,
        is_donor_tagged: inv.is_donor_tagged ?? false,
        delink_log: inv.delink_log ?? null,
        patientInfo:
          `${inv.patient_mrn_no || ''}${inv.patient_mrn_no && inv.gender ? ' | ' : ''}${inv.gender || ''}`
            .trim() || '-',
        clinic_id: inv.clinic_id != null && !isNaN(Number(inv.clinic_id)) ? Number(inv.clinic_id) : null,
        advance_consumption_metadata: inv.advance_consumption_metadata ?? null,
        payment_count: inv.payment_count ?? 0,
      })),
    [displayedInvoices]
  );

  // Map dispensed items to table format (UAE flow - same format as regular invoices)
  const dispensedTableData = useMemo(
    () =>
      dispensedItems.map(inv => ({
        patient_id: inv.patient_id,
        appointment_id: inv.appointment_id ?? null,
        id: inv.id ?? null,
        patientId: inv.patient_id,
        mrn: inv.patient_mrn_no ?? undefined,
        appointmentId: inv.appointment_id ?? null,
        date: inv.visit_date || '-',
        patientName: `${inv.patient ?? inv?.patient_name ?? ''}${inv.age ? ` | ${inv.age}` : ''}` || '-',
        status: normalizeStatus(inv.status || inv.type || ''),
        billNo: inv.bill_number || '-',
        clinic: inv?.clinic || '-',
        billType: mapBillTypeLabel(inv.bill_type),
        provider: inv.Provider || '-',
        invoiceDueStatus: inv.invoice_due_status ?? '',
        totalAmt: inv.total_amount != null ? String(inv.total_amount) : '-',
        concessionAmt: inv.concession_amount != null ? String(inv.concession_amount) : '-',
        netAmt: inv.net_amount != null ? String(inv.net_amount) : '-',
        paidAmt: inv.paid_amount != null ? String(inv.paid_amount) : '-',
        balanceAmt: inv.balance_amount != null ? String(inv.balance_amount) : '-',
        billRemark: inv.remark,
        actions: '',
        paymentSource: inv.payment_source ?? null,
        label: inv.label ?? null,
        labelMrnNo: inv.label_mrn_no ?? null,
        can_settle_invoice: inv.can_settle_invoice ?? true,
        invoice_patient_type: inv.invoice_patient_type ?? null,
        is_donor_tagged: inv.is_donor_tagged ?? false,
        delink_log: inv.delink_log ?? null,
        patientInfo:
          `${inv.patient_mrn_no || ''}${inv.patient_mrn_no && inv.gender ? ' | ' : ''}${inv.gender || ''}`
            .trim() || '-',
        clinic_id: inv.clinic_id != null && !isNaN(Number(inv.clinic_id)) ? Number(inv.clinic_id) : null,
        advance_consumption_metadata: inv.advance_consumption_metadata ?? null,
      })),
    [dispensedItems]
  );

  // Error alert 
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Invoice fetch error', error);
    }
  }, [error]);

  // Derived: whether any filter is active
  const isFiltered = useMemo(
    () => !!(filterValues.fromDate || filterValues.toDate || filterValues.billType),
    [filterValues]
  );

  // Build a compact summary label for the active filters
  const filterSummaryLabel = useMemo(() => {
    const parts: string[] = [];
    if (filterValues.fromDate) parts.push(`From: ${formatDateDMY(filterValues.fromDate)}`);
    if (filterValues.toDate) parts.push(`To: ${formatDateDMY(filterValues.toDate)}`);
    if (filterValues.billType) {
      const typeLabel = filterValues.billType === 'PH' ? 'Pharmacy' : filterValues.billType;
      parts.push(`Type: ${typeLabel}`);
    }
    return parts.join(' • ');
  }, [filterValues]);

  // Sync bill_type from URL on mount (preserve filter on refresh/navigation) 
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bill = params.get('bill_type');
    if (!bill) return;
    
    // normalize: accept 'PH' or 'Pharmacy' or 'Clinical' or 'Order'
    let mapped = bill;
    if (bill.toLowerCase() === 'ph') mapped = 'PH';
    if (bill.toLowerCase() === 'pharmacy') mapped = 'Pharmacy';
    if (bill.toLowerCase() === 'clinical') mapped = 'Clinical';
    if (bill.toLowerCase() === 'order') mapped = 'Order';
    
    // Only update if different from current value
    if (mapped !== filterValues.billType) {
      setFilterValues((prev) => ({ ...prev, billType: mapped }));
      setCurrentPage(1);
    }
  }, [location.search, filterValues.billType]);

  // --- Render --- 
  return (
    <>
      <HeaderProfile />

      {/* Unsettled Donor Bill Detected — self-contained component */}
      <UnsettledDonorBillDetector currencySymbol={currencySymbol} />
    
      <Box className="invoice-page">
        {/* Header: Tabs + Filter + New Invoice on same line */}
        <Stack 
          direction="row" 
          alignItems="center" 
          justifyContent="space-between" 
          spacing={2} 
          sx={{ mb: 2, px: 2, mt: 2 }}
        >
          {/* Left: Bill-type tabs */}
          <InvoiceCategoryTabs
            activeCategory={activeTab}
            onCategoryChange={(cat) => {
              const selectedName = cat.name as string;
              setActiveTab(selectedName);
              
              // Reset selected services/drugs when switching tabs
              if (selectedName !== 'By Services') {
                setSelectedIndices(new Set());
              }
              if (selectedName !== 'By Pharmacy') {
                setSelectedDrugIndices(new Set());
              }
              
              // map selected tab to bill_type value
              let mapped = '';
              if (selectedName === 'By Order') mapped = ''; // By Order = no filter (all)
              // if (selectedName === 'By Clinical') mapped = 'Clinical'; // TEMPORARILY COMMENTED OUT
              if (selectedName === 'By Pharmacy') mapped = 'PH'; // By Pharmacy = PH
              if (selectedName === 'By Services') mapped = ''; // By Services = special handling

              setFilterValues((prev) => ({ ...prev, billType: mapped }));
              const params = new URLSearchParams(location.search);
              if (mapped) params.set('bill_type', mapped); else params.delete('bill_type');
              navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
              setCurrentPage(1);
            }}
            categories={useMemo(() => {
              // Get counts from API meta for bill types  
              const billTypeCounts = data?.meta?.bill_type_count;
              return [
                { name: 'By Order', label: 'By Order', count: billTypeCounts?.total ?? 0 }, // By Order = all invoices
                // { name: 'By Clinical', label: 'By Clinical', count: billTypeCounts?.clinical ?? 0 }, // TEMPORARILY COMMENTED OUT
                { name: 'By Services', label: 'By Services', count: billTypeCounts?.services ?? 0 }, // By Services count from API
                { name: 'By Pharmacy', label: 'By Pharmacy', count: billTypeCounts?.pharmacy ?? 0 } // By Pharmacy = PH
              ];
            }, [data?.meta?.bill_type_count])}
          />

          {/* Right: Filter + New Invoice button - hidden on "By Services" tab */}
          {activeTab !== 'By Services' && activeTab !== 'By Pharmacy' && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Tooltip title="Filters" arrow>
                <IconButton 
                  aria-label="open filters" 
                  onClick={handleOpenLegacyFilter} 
                  sx={{ width: 40, height: 40, p: 0 }}
                >
                  <FunnelIcon style={{ width: 40, height: 40 }} />
                </IconButton>
              </Tooltip>
              <NewInvoiceButton onClick={handleNewInvoice} label="New Invoice" />
            </Stack>
          )}

          {/* Create Invoice button - shown only on "By Services" tab */}
          {activeTab === 'By Services' && (
            <NewInvoiceButton onClick={handleCreateInvoiceWithServices} label="New Invoice" />
          )}

          {/* Add Pharmacy Item button - shown only on "By Pharmacy" tab */}
          {activeTab === 'By Pharmacy' && (
            <NewInvoiceButton onClick={handleNavigateToPharmacyDispense} label="New Invoice" />
          )}
        </Stack>

        {/* Active filter badge */}
        {isFiltered && activeTab !== 'By Services' && activeTab !== 'By Pharmacy' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 1 }}>
            <Chip
              label={filterSummaryLabel || 'Filters active'}
              color="primary"
              variant="outlined"
              onDelete={() => {
                setFilterValues({ fromDate: '', toDate: '', billType: '' });
                setCurrentPage(1);
              }}
              sx={{
                borderRadius: '16px',
                fontSize: '0.8rem',
                maxWidth: '100%',
              }} />
          </Box>
        )}

        {/* Legacy Modal */}
        <InvoiceFilter
          open={filterOpenLegacy}
          onClose={handleCloseLegacyFilter}
          onApply={handleApplyFilter}
          billTypeOptions={[
            { value: '', label: 'Select Bill Type' },
            { value: 'Clinical', label: 'Clinical' },
            // UI shows "Pharmacy" but backend expects bill_type = 'PH'
            { value: 'PH', label: 'Pharmacy' },
            { value: 'Package', label: 'Package' },
          ]}
          // Pass current filters so reopening shows previous applied values
          initialValues={filterValues} />

        {/* Conditional rendering based on active tab */}
        {activeTab === 'By Services' ? (
          /* Service Selection Table for "By Services" tab */
          <Box sx={{ px: 2 }}>
            <UnbilledServicesTable
              services={unbilledServices}
              isLoading={unbilledServicesLoading}
              currencySymbol={currencySymbol}
              selectedIndices={selectedIndices}
              onServiceToggle={handleServiceToggle}
              onSelectAllServices={handleSelectAllServices}
            />
          </Box>
        ) : activeTab === 'By Pharmacy' ? (
          /* Pharmacy Tab - shows different content based on region */
          <Box sx={{ px: 2 }}>
            {isUAEDispenseFlow ? (
              /* UAE Flow: Use same InvoiceTable with dispensed items filter */
              <InvoiceTable 
                key="dispensed-pharmacy"
                patientId={patientId} 
                data={dispensedTableData} 
                loading={pharmacyTabLoading} 
              />
            ) : (
              /* India Flow: Patient Drugs Table (Prescribed Items) */
              <PatientDrugsTable
                drugs={patientDrugs}
                isLoading={pharmacyTabLoading}
                selectedIndices={selectedDrugIndices}
                onDrugToggle={handleDrugToggle}
                onSelectAllDrugs={handleSelectAllDrugs}
              />
            )}
          </Box>
        ) : (
          /* Regular Invoice Table */
          <>
            <InvoiceTable
              key={`${filterValues.billType}-${selectedStatus?.value}-${currentPage}`}
              patientId={patientId} 
              data={tableData} 
              loading={isLoading} 
            />

            {/* Pagination & Status Badges */}
            <InvoicePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalEntries={data?.meta.total_count || 0}
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 25, 50]}
              onPageSizeChange={handlePageSizeChange}
              statusBadges={<InvoiceStatusChipGroup
                summary={statusSummary}
                selectedStatus={selectedStatus}
                onStatusSelect={handleStatusSelect} />}
              rightContent={<ResetButton onClick={handleReset} sx={{ ml: 1, height: '30px' }} />} />
          </>
        )}
      </Box>
    </>
  );
};

export default PatientInvoice;
